/** Volunteer UI configs: statuses, filters, tabs. */
import { getRoleLabel } from '@/utils/role-label-utils';

/** Volunteer list filter state. */
export interface VolunteerFilters {
  /** Name, first name, email or phone. */
  search?: string;
  statuses: string[];
  subjects: string[];
  availability?: string[];
  establishmentId?: string;
  dateRange?: 'all' | 'week' | 'month' | 'year';
  customDateFrom?: Date;
  customDateTo?: Date;
  entryDateFrom?: string;
  entryDateTo?: string;
  engagementStartDateFrom?: string;
  engagementStartDateTo?: string;
  engagementEndDateFrom?: string;
  engagementEndDateTo?: string;
  exitDateFrom?: string;
  exitDateTo?: string;
  exitReason?: string;
}

/** Default volunteer list filters. */
export const DEFAULT_VOLUNTEER_FILTERS: VolunteerFilters = {
  search: undefined,
  statuses: [],
  subjects: [],
  availability: [],
  establishmentId: undefined,
  dateRange: 'all',
  customDateFrom: undefined,
  customDateTo: undefined,
  entryDateFrom: undefined,
  entryDateTo: undefined,
  engagementStartDateFrom: undefined,
  engagementStartDateTo: undefined,
  engagementEndDateFrom: undefined,
  engagementEndDateTo: undefined,
  exitDateFrom: undefined,
  exitDateTo: undefined,
  exitReason: undefined,
};

/** Filter group IDs for the volunteers {@link DataTable} configure panel. */
export type FilterGroupId =
  | 'filter-search'
  | 'filter-status'
  | 'filter-subject'
  | 'filter-availability'
  | 'filter-establishment'
  | 'filter-dates'
  | 'filter-entry-date'
  | 'filter-mission-start-date'
  | 'filter-contract-end-date'
  | 'filter-exit-date'
  | 'filter-exit-reason';

/** Configure-panel order for volunteer filter groups. */
export const ALL_FILTER_GROUP_IDS: FilterGroupId[] = [
  'filter-search',
  'filter-status',
  'filter-subject',
  'filter-availability',
  'filter-establishment',
  'filter-dates',
  'filter-entry-date',
  'filter-mission-start-date',
  'filter-contract-end-date',
  'filter-exit-date',
  'filter-exit-reason',
];

/** Tab colors for the Volunteers section and management page. Roles are dynamic; only the All tab is static. */
export const VOLUNTEER_TAB_COLORS: Record<string, string> = {
  all: 'var(--color-5-nuance-1)',
};

/** {@link Select} options for volunteer exit reason. */
export const VOLUNTEER_EXIT_REASON_OPTIONS = [
  { value: "Fin d'engagement anticipé", label: "Fin d'engagement anticipé" },
  { value: 'Réaffectation dans une autre zone', label: 'Réaffectation dans une autre zone' },
  { value: 'Ne donne pas suite', label: 'Ne donne pas suite' },
  { value: 'Autre', label: 'Autre' },
] as const;

/** Human-readable label per filter group (configure panel). */
export const FILTER_GROUP_LABELS: Record<FilterGroupId, string> = {
  'filter-search': 'Recherche',
  'filter-status': 'Statut',
  'filter-subject': 'Matière',
  'filter-availability': 'Disponibilité',
  'filter-establishment': 'Établissement',
  'filter-dates': 'Date de création',
  'filter-entry-date': "Date d'entrée",
  'filter-mission-start-date': 'Date de début engagement',
  'filter-contract-end-date': 'Date de fin engagement',
  'filter-exit-date': 'Date de désactivation',
  'filter-exit-reason': 'Motif de sortie',
};

/** React Query keys invalidated after a volunteer create / save. */
export const VOLUNTEER_FORM_QUERY_KEYS = {
  roles: ['roles'] as const,
  subjects: ['subjects'] as const,
  volunteer: (id: string) => ['users', 'volunteer', id] as const,
};

export {
  DATE_RANGE_PERIOD_OPTIONS,
  GENDER_OPTIONS,
  STAFF_STATUS_CONFIG,
  STAFF_STATUS_FORM_OPTIONS,
  WEEKDAY_FILTER_OPTIONS,
} from '@/constants/shared-catalog';

/** Default role label shown on the volunteer create form. */
export function getVolunteerFormDefaultRoleLabel(): string {
  return getRoleLabel('volunteer');
}
